import { useCallback, useMemo, useSyncExternalStore } from "react";

/**
 * External store for high-frequency table header UI state:
 *   - which dropdown menu is open
 *   - current sort column + direction
 *   - the per-column "checked labels" set that drives the filter-menu UI
 *
 * Why not useState?
 *   Keeping these in AssociateFee's React state made every open/close,
 *   every sort toggle, and every filter-checkbox click re-render the
 *   entire parent (which rebuilds `columnsWithMenus` and walks every
 *   header cell even though only one was affected).
 *
 *   Moving them to an external ref-based store isolates the update path:
 *   only the AssociateFeeHeaderMenu instance whose key actually changed
 *   re-renders (via useSyncExternalStore); everything else stays
 *   completely untouched.
 *
 * Scope: purely UI interaction state.
 *   Data-driving state (the filter map used for row filtering, sort key
 *   used for row sorting) should STILL live in React state, updated
 *   inside startTransition so heavy recomputation is deferred — see
 *   Pattern 4 in .agents/skills/react-table-perf/SKILL.md.
 */
export function useHeaderMenuStore() {
  return useMemo(() => {
    let openKey = null;
    let sortKey = null;
    let sortDir = null;
    /** @type {Record<string, Set<string>>} */
    let filters = {};
    const listeners = new Set();
    const notify = () => listeners.forEach((l) => l());

    return {
      subscribe: (listener) => {
        listeners.add(listener);
        return () => listeners.delete(listener);
      },
      // Menu open/close
      getOpenKey: () => openKey,
      setOpenKey: (key) => {
        const next = key ?? null;
        if (openKey === next) return;
        openKey = next;
        notify();
      },
      // Sort state
      getSortKey: () => sortKey,
      getSortDir: () => sortDir,
      setSort: (key, dir) => {
        const nextKey = key ?? null;
        const nextDir = dir ?? null;
        if (sortKey === nextKey && sortDir === nextDir) return;
        sortKey = nextKey;
        sortDir = nextDir;
        notify();
      },
      // Filter selection state (per-column Set<label>)
      getFilters: () => filters,
      getFilter: (key) => filters[key] ?? null,
      setFilter: (key, value) => {
        const current = filters[key] ?? null;
        const next = value ?? null;
        if (current === next) return;
        const copy = { ...filters };
        if (next === null) delete copy[key];
        else copy[key] = next;
        filters = copy;
        notify();
      },
      clearFilters: () => {
        if (Object.keys(filters).length === 0) return;
        filters = {};
        notify();
      },
    };
  }, []);
}

/**
 * Per-column selector — only re-renders when THIS column's open/close
 * boolean actually flips, not when any other menu opens.
 */
export function useIsMenuOpen(store, key) {
  const getSnapshot = useCallback(() => store.getOpenKey() === key, [store, key]);
  return useSyncExternalStore(store.subscribe, getSnapshot);
}

/**
 * Per-column sort selector — only re-renders when THIS column's sort
 * direction actually changes (null ↔ "asc" ↔ "desc").
 */
export function useSortDirection(store, key) {
  const getSnapshot = useCallback(
    () => (store.getSortKey() === key ? store.getSortDir() : null),
    [store, key],
  );
  return useSyncExternalStore(store.subscribe, getSnapshot);
}

/**
 * Per-column filter selector — only re-renders when THIS column's
 * selected-label Set changes.  Returns the Set or null.
 *
 * Snapshots a primitive Set reference, so shallow equality works and
 * React will not loop (the store preserves the same Set reference when
 * nothing changed).
 */
export function useFilterValue(store, key) {
  const getSnapshot = useCallback(() => store.getFilter(key), [store, key]);
  return useSyncExternalStore(store.subscribe, getSnapshot);
}

/**
 * Parent-level sort accessor — re-renders the parent only when the
 * sort key or direction actually changes (needed for sortedRows computation).
 */
export function useSortConfig(store) {
  return [
    useSyncExternalStore(store.subscribe, store.getSortKey),
    useSyncExternalStore(store.subscribe, store.getSortDir),
  ];
}
